import { AuthError, useAuth } from '@care-platform/auth';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@care-platform/ui';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useCopy } from './copy';

/** Local development sign-in: paste a token from service-kit's development issuer. */
function DevelopmentSignIn() {
  const copy = useCopy();
  const auth = useAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const reason = auth.state.status === 'signedOut' ? auth.state.reason : undefined;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await auth.signIn({ token });
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof AuthError && caught.code === 'expired'
          ? copy.signIn.expired
          : copy.signIn.malformed,
      );
    }
  };

  return (
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle className="text-[1.25rem]">{copy.signIn.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {reason && (
          <Alert variant="warning">
            <AlertDescription>
              {reason === 'expired' ? copy.signIn.expired : copy.signIn.rejected}
            </AlertDescription>
          </Alert>
        )}
        <form onSubmit={(event) => void submit(event)} className="space-y-3">
          <p className="text-sm font-semibold text-brand-navy">{copy.signIn.developmentHeading}</p>
          <p className="text-sm text-muted-foreground">{copy.signIn.developmentHelp}</p>
          <div>
            <Label htmlFor="dev-token">{copy.signIn.tokenLabel}</Label>
            <textarea
              id="dev-token"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              rows={4}
              spellCheck={false}
              autoComplete="off"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'dev-token-error' : undefined}
              className="w-full rounded-sm border border-input bg-white p-2 font-mono text-xs outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
            />
            {error && (
              <p id="dev-token-error" className="mt-1 text-sm text-missed-text">
                {error}
              </p>
            )}
          </div>
          <Button type="submit" disabled={token.trim().length === 0}>
            {copy.signIn.submit}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * PLACEHOLDER FOR P1, Development only: email and password, checked by the BFF's placeholder sign-in. Replaced by
 * the identity provider's sign-in when it is chosen.
 */
function PasswordSignIn() {
  const copy = useCopy();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = auth.state.status === 'signedOut' ? auth.state.reason : undefined;
  const text = copy.signIn.password;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await auth.signIn({ email: email.trim(), password });
    } catch (caught) {
      const code = caught instanceof AuthError ? caught.code : 'unavailable';
      setError(
        code === 'invalidCredentials'
          ? text.invalidCredentials
          : code === 'throttled'
            ? text.throttled
            : text.unavailable,
      );
      setPassword('');
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      <div className="mb-6 flex items-baseline justify-center gap-2">
        <span className="text-[1.5rem] font-bold text-brand-navy">{copy.product.owner}</span>
        <span className="text-[1.5rem] text-brand-blue">{copy.product.name}</span>
      </div>
      <Card className="shadow-sm">
        <CardHeader className="pt-6 pb-2">
          <CardTitle className="text-[1.25rem]">
            <h1>{text.heading}</h1>
          </CardTitle>
          <p className="text-sm text-muted-foreground">{text.intro}</p>
        </CardHeader>
        <CardContent className="space-y-4 pb-6">
          {reason && !error && (
            <Alert variant="warning">
              <AlertDescription>{copy.signIn.rejected}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="critical">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="sign-in-email">{text.emailLabel}</Label>
              <Input
                id="sign-in-email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-invalid={error ? true : undefined}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sign-in-password">{text.passwordLabel}</Label>
              <Input
                id="sign-in-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={error ? true : undefined}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={submitting || email.trim().length === 0 || password.length === 0}
            >
              {submitting ? text.submitting : text.submit}
            </Button>
          </form>
        </CardContent>
      </Card>
      <p className="mt-4 text-center text-xs text-muted-foreground">{text.environmentNote}</p>
    </div>
  );
}

function Unconfigured() {
  const copy = useCopy();
  return (
    <Alert variant="warning" className="max-w-lg">
      <AlertTitle>{copy.signIn.unconfiguredTitle}</AlertTitle>
      <AlertDescription>{copy.signIn.unconfiguredBody}</AlertDescription>
    </Alert>
  );
}

/** Authentication belongs to the shell, not a module (I §6). Renders children only for a signed-in user. */
export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.state.status === 'signedIn') return <>{children}</>;
  return (
    <div className="flex min-h-screen items-start justify-center bg-chrome px-4 pt-24">
      {auth.state.status === 'unconfigured' ? (
        <Unconfigured />
      ) : auth.state.status === 'loading' ? null : auth.method === 'password' ? (
        <PasswordSignIn />
      ) : (
        <DevelopmentSignIn />
      )}
    </div>
  );
}
